import {
  runCreateResourceFolderWorkflow,
  runDeleteResourceFolderWorkflow,
  runRenameResourceFolderWorkflow,
} from "@/action-dialog";
import { useFolders } from "@/api/folders";
import { useCheckBulkPermissions, useListRelationships } from "@/api/relations";
import { useWorkspaceCanvases } from "@/api/workspaces";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { ROUTES } from "@/constants/routes";
import { RailWorkspaceExpandableRow } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailExpandableRow";
import {
  RailRowMenu,
  railRowMenuProps,
} from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailRowMenu";
import { RailSectionFolderRow } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailSectionFolderRow";
import { RailSectionLoading } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailSectionLoading";
import { RailZeroState } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailZeroState";
import { useRailExpansion } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/useRailExpansion";
import { useRailRowContextMenu } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/useRailRowContextMenu";
import {
  CanvasFolderDropTarget,
  WorkspacesRailCanvasFolderTree,
  type CanvasFolderActions,
} from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/workspaces/WorkspacesRailCanvasFolderTree";
import { WorkspacesRailCanvasNode } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/workspaces/WorkspacesRailCanvasNode";
import { WorkspacesRailChatsSection } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/workspaces/WorkspacesRailChatsSection";
import { WorkspacesRailFilesSection } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/workspaces/WorkspacesRailFilesSection";
import {
  ObjectActionMenu,
  createWorkspaceActionHandlers,
  useObjectActions,
  type ObjectKind,
} from "@/object-actions";
import { route } from "@/utils/route";
import {
  FOLDER_RESOURCE_TYPE_CANVAS,
  type Canvas,
  type OrganizationUser,
  type Workspace,
} from "@cohesive/api-client";
import { Icon as WorkspaceMetadataIcon } from "@cohesive/ui-components";
import {
  FolderPlus,
  Frame,
  Layers,
  Plus,
  UserPlus,
  Users,
  type LucideIcon,
} from "lucide-react";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";

const WORKSPACE_TEAM_KIND: ObjectKind = { type: "workspace", scope: "team" };
const WORKSPACE_PRIVATE_KIND: ObjectKind = {
  type: "workspace",
  scope: "private",
};

function getMemberInitials(user: OrganizationUser): string {
  const first = user.first_name?.[0];
  const last = user.last_name?.[0];
  if (first || last) return `${first ?? ""}${last ?? ""}`.toUpperCase();
  return (
    user.name
      ?.split(/\s+/)
      .map((segment) => segment[0])
      .filter(Boolean)
      .join("")
      .slice(0, 2)
      .toUpperCase() || "?"
  );
}

function WorkspaceMembersIndicator({
  workspace,
  members,
  onShare,
}: {
  workspace: Workspace;
  members: OrganizationUser[];
  onShare: () => void;
}) {
  return (
    <HoverCard openDelay={500} closeDelay={100}>
      <HoverCardTrigger asChild>
        <span
          role="img"
          aria-label={`Shared workspace — ${members.length} ${members.length === 1 ? "member" : "members"}`}
          className="inline-flex size-4 shrink-0 items-center justify-center text-n-50 hover:text-n-80"
          onClick={(e) => e.stopPropagation()}
        >
          <Users className="size-3.5" />
        </span>
      </HoverCardTrigger>
      <HoverCardContent
        side="right"
        align="start"
        sideOffset={8}
        collisionPadding={20}
        className="w-[260px] p-2 rounded-2xl border dark:border-white/[0.08] shadow-xl shadow-black/10 bg-background/95 backdrop-blur-lg"
      >
        <div className="flex items-center justify-between gap-2 pl-3 pr-1.5 pt-1.5 pb-1">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/70">
            Members
          </p>
          <button
            type="button"
            aria-label="Add members"
            onClick={(e) => {
              e.stopPropagation();
              window.setTimeout(onShare, 0);
            }}
            className="flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-foreground/[0.05] dark:hover:bg-n-95/[0.05] cursor-pointer"
          >
            <UserPlus className="h-4 w-4" />
          </button>
        </div>
        <div className="my-1 h-px bg-border/40 dark:bg-n-95/[0.06]" />
        <ul className="max-h-64 overflow-y-auto -mr-1 pr-1">
          {members.map((m) => (
            <li
              key={m.id}
              className="flex items-center gap-2.5 h-10 rounded-lg px-3"
            >
              <Avatar className="h-7 w-7 shrink-0">
                {m.picture_url && (
                  <AvatarImage src={m.picture_url} alt={m.name} />
                )}
                <AvatarFallback className="text-[11px] font-medium bg-foreground/[0.06] dark:bg-n-95/[0.06] text-foreground">
                  {getMemberInitials(m)}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <p className="truncate text-[13px] font-medium text-foreground leading-tight">
                  {m.name}
                </p>
                {m.id === workspace.user_id && (
                  <p className="text-[11px] text-muted-foreground truncate mt-0.5 leading-tight">
                    Owner
                  </p>
                )}
              </div>
            </li>
          ))}
        </ul>
      </HoverCardContent>
    </HoverCard>
  );
}

export function WorkspacesRailWorkspaceRow({
  workspace,
  active,
  activeCanvasId,
  currentUserId,
  usersById,
  onRename,
  onShare,
  onDelete,
  onRenameCanvas,
  onMoveCanvas,
  onShareCanvas,
  onDeleteCanvas,
  onNewCanvas,
  onNewChat,
  onOpen,
  newCanvasDisabled,
  labelOverride,
  icon: Icon = Layers,
  trailingSlot,
  hideWorkspaceMenu,
  defaultOpen = false,
}: {
  workspace: Workspace;
  active?: boolean;
  activeCanvasId: string | null;
  currentUserId?: string;
  usersById: Map<string, OrganizationUser>;
  onRename: () => void;
  onShare: () => void;
  onDelete?: () => void;
  onRenameCanvas: (canvas: Canvas) => void;
  onMoveCanvas: (canvas: Canvas) => void;
  onShareCanvas: (canvas: Canvas) => void;
  onDeleteCanvas: (canvas: Canvas) => void;
  /**
   * Create a new canvas in this workspace. Called with no argument from the
   * Canvases section kebab (root), or with a `folderId` from a folder's kebab.
   */
  onNewCanvas?: (folderId?: string) => void;
  /** Create a new workspace chat (root or, with a `folderId`, inside a folder). */
  onNewChat?: (folderId?: string) => void;
  /** Navigate to the workspace detail page (title double-click; also in the
   *  kebab/right-click menu — a single title click toggles expansion). */
  onOpen?: () => void;
  newCanvasDisabled?: boolean;
  labelOverride?: string;
  icon?: LucideIcon;
  trailingSlot?: React.ReactNode;
  hideWorkspaceMenu?: boolean;
  /** Expand the row (revealing its sections) on first render. */
  defaultOpen?: boolean;
}) {
  const menu = useRailRowContextMenu();
  const canvasesMenu = useRailRowContextMenu();
  const navigate = useNavigate();
  const [open, setOpen] = useRailExpansion(`ws:${workspace.id}`, defaultOpen);

  const { data: canvases = [], isLoading: canvasesLoading } =
    useWorkspaceCanvases(workspace.id, {
      enabled: open,
    });

  const { data: folders = [], isLoading: foldersLoading } = useFolders(
    workspace.id,
    { enabled: open },
  );
  const canvasesSectionLoading = canvasesLoading || foldersLoading;

  const openNewFolder = useCallback(
    (parentFolderId: string | null) => {
      void runCreateResourceFolderWorkflow({
        workspaceId: workspace.id,
        resourceType: FOLDER_RESOURCE_TYPE_CANVAS,
        parentFolderId,
      });
    },
    [workspace.id],
  );

  const folderActions = useMemo<CanvasFolderActions>(
    () => ({
      // Open = the folder's view on the workspace detail page (same target as
      // the detail-page folder menus).
      onOpen: (folder) =>
        navigate(
          route(
            ROUTES.WORKSPACES.DETAIL,
            { workspaceId: workspace.id },
            { canvasFolder: folder.id },
          ),
        ),
      onNewSubfolder: (parentFolderId) => openNewFolder(parentFolderId),
      onNewResource: (folderId) => onNewCanvas?.(folderId),
      onRename: (folder) => {
        void runRenameResourceFolderWorkflow({
          workspaceId: workspace.id,
          folder,
        });
      },
      onDelete: (folder) => {
        void runDeleteResourceFolderWorkflow({
          workspaceId: workspace.id,
          folder,
        });
      },
    }),
    [workspace.id, openNewFolder, onNewCanvas, navigate],
  );

  // Every workspace row gets a kebab (+ matching right-click menu) that leads
  // with the top-level create actions (New Canvas / New chat — folder-scoped
  // creation lives on each section's own kebab). Team workspaces add the
  // management actions (Edit / Add Members / Archive); the private
  // "My Workspace" can't be edited/shared/trashed, so its menu is create-only.
  const workspaceKind = hideWorkspaceMenu
    ? WORKSPACE_PRIVATE_KIND
    : WORKSPACE_TEAM_KIND;

  const workspaceActionHandlers = useMemo(
    () =>
      createWorkspaceActionHandlers(workspace, {
        newCanvas:
          onNewCanvas && !newCanvasDisabled ? () => onNewCanvas() : undefined,
        newChat: onNewChat ? () => onNewChat() : undefined,
        openWorkspace: onOpen ? () => onOpen() : undefined,
        ...(hideWorkspaceMenu
          ? {}
          : {
              editWorkspace: () => onRename(),
              shareWorkspace: () => onShare(),
              deleteWorkspace: onDelete ? () => onDelete() : undefined,
            }),
      }),
    [
      workspace,
      hideWorkspaceMenu,
      onNewCanvas,
      onNewChat,
      onOpen,
      newCanvasDisabled,
      onRename,
      onShare,
      onDelete,
    ],
  );

  const { actions: workspaceActions } = useObjectActions({
    kind: workspaceKind,
    surface: "left-rail",
    enabled: menu.open,
    handlers: workspaceActionHandlers,
  });

  const canvasPermissionChecks = useMemo(
    () =>
      canvases.flatMap((canvas) => [
        {
          resource_object_type: "canvas" as const,
          resource_object_id: canvas.id,
          permission: "can_delete" as const,
        },
        {
          resource_object_type: "canvas" as const,
          resource_object_id: canvas.id,
          permission: "can_manage_assignments" as const,
        },
      ]),
    [canvases],
  );

  const { data: canvasPermissions } = useCheckBulkPermissions({
    checks: canvasPermissionChecks,
  });

  const { data: relationships } = useListRelationships(
    "workspace",
    workspace.id,
  );

  const members = useMemo<OrganizationUser[]>(() => {
    if (!relationships) return [];
    const memberIds = new Set<string>();
    memberIds.add(workspace.user_id);
    for (const rel of relationships) {
      if (rel.subject_object_type === "organization_user") {
        memberIds.add(rel.subject_object_id);
      }
    }
    return Array.from(memberIds)
      .map((id) => usersById.get(id))
      .filter((u): u is OrganizationUser => !!u);
  }, [relationships, usersById, workspace.user_id]);

  const isSharedWithMe = !!currentUserId && workspace.user_id !== currentUserId;
  const showMembersIndicator = isSharedWithMe || members.length > 1;

  const workspaceMenuNode = (
    <RailRowMenu
      {...railRowMenuProps(menu)}
      label={labelOverride ?? workspace.name}
      contentWidthClassName="w-52"
    >
      <ObjectActionMenu
        actions={workspaceActions}
        kind={workspaceKind}
        name={workspace.name}
      />
    </RailRowMenu>
  );

  // Canvases-section kebab: New canvas + New folder (folder at the workspace root).
  const canvasesMenuNode = (
    <RailRowMenu {...railRowMenuProps(canvasesMenu)} label="Canvases">
      {onNewCanvas && (
        <DropdownMenuItem
          disabled={newCanvasDisabled}
          onClick={(e) => {
            e.stopPropagation();
            window.setTimeout(onNewCanvas, 0);
          }}
        >
          <Plus />
          New Canvas
        </DropdownMenuItem>
      )}
      <DropdownMenuItem
        onClick={(e) => {
          e.stopPropagation();
          window.setTimeout(() => openNewFolder(null), 0);
        }}
      >
        <FolderPlus />
        New folder
      </DropdownMenuItem>
    </RailRowMenu>
  );

  return (
    <RailWorkspaceExpandableRow
      icon={Icon}
      // Prefer the workspace's own chosen (colored) icon; fall back to the
      // generic Lucide icon when none is set.
      iconNode={
        workspace.metadata?.icon ? (
          <WorkspaceMetadataIcon icon={workspace.metadata.icon} size={16} />
        ) : undefined
      }
      label={labelOverride ?? workspace.name}
      active={active}
      open={open}
      onOpenChange={setOpen}
      onOpen={onOpen}
      trailing={
        showMembersIndicator || trailingSlot ? (
          <span className="inline-flex items-center gap-1">
            {showMembersIndicator ? (
              <WorkspaceMembersIndicator
                workspace={workspace}
                members={members}
                onShare={onShare}
              />
            ) : null}
            {trailingSlot}
          </span>
        ) : undefined
      }
      menu={workspaceMenuNode}
      onContextMenu={menu.onContextMenu}
    >
      {open ? (
        // The expanded workspace is the §5.1 typed-containment tree: its
        // Canvases, Chats, and Files rendered as three folder rows (same
        // styling as real folders), each keeping its own kebab. Mounted only
        // while open so the Chats/Files queries and dialogs stay lazy.
        //
        // Drop zones are section/row-scoped (no whole-workspace zone): each
        // section is a typed organizing surface (drop here = move to root /
        // un-folder; wrong kinds are rejected), folders accept their own kind,
        // and individual canvas/chat rows accept files (copy-into-context).
        <div className="flex flex-col">
          {/* The whole Canvases folder (header + content) is the canvases
              organizing surface (canvas_folder, folderId=null): dropping a
              canvas here moves it to the workspace root / un-folders it. It's
              inert for non-canvas drags and works even when collapsed. Folder
              headers + canvas rows inside are smaller targets that win on hover. */}
          <CanvasFolderDropTarget workspaceId={workspace.id} folderId={null}>
            <RailSectionFolderRow
              label="Canvases"
              icon={Frame}
              expansionKey={`ws:${workspace.id}:canvases`}
              onContextMenu={canvasesMenu.onContextMenu}
              menu={canvasesMenuNode}
              data-onboarding-target="rail-canvases-section"
            >
              {canvasesSectionLoading ? (
                <RailSectionLoading label="canvases" />
              ) : canvases.length === 0 && folders.length === 0 ? (
                <RailZeroState
                  icon={Frame}
                  accent="teal"
                  variant="passive"
                  title="No canvases yet."
                  description="Workspace canvases appear here."
                />
              ) : (
                <WorkspacesRailCanvasFolderTree
                  workspaceId={workspace.id}
                  parentFolderId={null}
                  folders={folders}
                  canvases={canvases}
                  folderActions={folderActions}
                  renderCanvasRow={(canvas) => (
                    <WorkspacesRailCanvasNode
                      key={canvas.id}
                      canvas={canvas}
                      dragNamespace={`ws:${workspace.id}`}
                      active={activeCanvasId === canvas.id}
                      onRename={onRenameCanvas}
                      onMove={onMoveCanvas}
                      onShare={
                        canvasPermissions?.canvas?.[canvas.id]
                          ?.can_manage_assignments
                          ? onShareCanvas
                          : undefined
                      }
                      onDelete={
                        canvasPermissions?.canvas?.[canvas.id]?.can_delete
                          ? onDeleteCanvas
                          : undefined
                      }
                    />
                  )}
                />
              )}
            </RailSectionFolderRow>
          </CanvasFolderDropTarget>
          <WorkspacesRailChatsSection
            workspaceId={workspace.id}
            onNewChat={onNewChat}
          />
          <WorkspacesRailFilesSection workspaceId={workspace.id} />
        </div>
      ) : null}
    </RailWorkspaceExpandableRow>
  );
}
